import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Tooltip, Typography } from '@mui/material';
import { makeObservable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { createRef } from 'react';
import ErrorUtil from '@/util/ErrorUtil';
import NumeroProcessoUtil, { ConfiguracaoPesquisaNumeroProcesso } from '@/util/NumeroProcessoUtil';
import ReactComponent from '../ReactComponent';

class FiltrarPorNumerosProcessos extends ReactComponent {
    SEPARADOR = /[,;|\t\n]|\s{4,}/;
    fileReader;
    inputRef;

    constructor(props) {
        super(props);
        makeObservable(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.fileReader = new FileReader();
        this.inputRef = createRef();
    }

    handleClick() {
        this.inputRef.current.click();
    }

    handleChange(event) {
        const file = event.target.files[0];

        this.fileReader.onerror = function () {
            console.error(this.fileReader.error);
            this.mensagemStore.abrirMensagemErro(
                `Falha no acesso/leitura do arquivo ${file.name}: ${ErrorUtil.getMensagemErro(this.fileReader.error)}`
            );
        };

        this.fileReader.onload = function (event) {
            const conteudo = event.target.result;
            const processosLidos = this.lerProcessos(conteudo);

            runInAction(() => {
                this.observableProps.storeListagemProcessos.filtros.filtroNumeroProcesso.valor = processosLidos.validos;

                let msg = { texto: '', duracao: undefined };
                msg = this.adicionarMensagem(
                    msg,
                    this.montarMsgFiltrados(
                        processosLidos.validos.length,
                        this.observableProps.storeListagemProcessos.processosFiltrados.length,
                        file.name
                    )
                );
                msg = this.adicionarMensagem(msg, this.montarMsgDuplicados(processosLidos.totalDuplicados));
                msg = this.adicionarMensagem(
                    msg,
                    this.montarMsgTextosDescartados(processosLidos.totalParticulasTextuaisDescartadas)
                );

                if (
                    processosLidos.validos.length > 0 &&
                    processosLidos.validos.length ===
                        this.observableProps.storeListagemProcessos.processosFiltrados.length &&
                    processosLidos.invalidos.length === 0
                ) {
                    this.mensagemStore.abrirMensagemSucesso(msg.texto, msg.duracao);
                } else {
                    msg = this.adicionarMensagem(msg, this.montarMsgInvalidos(processosLidos.invalidos));

                    const naoEncontrados = processosLidos.validos.filter((numeroProcesso) => {
                        const processosEncontrados =
                            this.observableProps.storeListagemProcessos.processosFiltrados.filter((registro) => {
                                const configuracaoPesquisa = new ConfiguracaoPesquisaNumeroProcesso(true, false, false);
                                return NumeroProcessoUtil.compararNumeroProcesso(
                                    registro.processo.numeroCompleto,
                                    numeroProcesso,
                                    configuracaoPesquisa
                                );
                            });

                        return processosEncontrados.length === 0;
                    });

                    msg = this.adicionarMensagem(
                        msg,
                        this.montarMsgNaoEncontrados(processosLidos.validos.length, naoEncontrados)
                    );

                    this.mensagemStore.abrirMensagemAlerta(msg.texto, msg.duracao);
                }
            });

            this.inputRef.current.value = null;
        }.bind(this);

        this.fileReader.readAsText(file);
    }

    lerProcessos(conteudo) {
        const numerosValidos = new Set();
        const numerosInvalidos = new Set();
        let totalNumerosValidos = 0;
        let totalParticulasTextuaisDescartadas = 0;

        const possiveisProcessos = conteudo.split(this.SEPARADOR);

        for (const possivelProcesso of possiveisProcessos) {
            if (!possivelProcesso) {
                continue;
            }

            const textoLimpo = this.tratarTexto(possivelProcesso);

            if (textoLimpo.length === 0) {
                continue;
            }

            if (/\d/.test(textoLimpo)) {
                if (NumeroProcessoUtil.validarNumeroProcesso(textoLimpo)) {
                    numerosValidos.add(NumeroProcessoUtil.formatarNumeroProcesso(textoLimpo, false, true));
                    totalNumerosValidos++;
                } else {
                    numerosInvalidos.add(textoLimpo);
                }
            } else {
                //console.log(`Texto descartado: ${possivelProcesso}.`);
                totalParticulasTextuaisDescartadas++;
            }
        }

        return {
            validos: Array.from(numerosValidos),
            invalidos: Array.from(numerosInvalidos),
            totalDuplicados: totalNumerosValidos - numerosValidos.size,
            totalParticulasTextuaisDescartadas: totalParticulasTextuaisDescartadas
        };
    }

    tratarTexto(texto) {
        let textoTratado = texto.trim();

        // Procura por delimitadores de texto para o formato CSV
        const QTD_MINIMA_CARACTERES = 2;
        if (
            textoTratado.length >= QTD_MINIMA_CARACTERES &&
            textoTratado.charAt(0) === '"' &&
            textoTratado.charAt(textoTratado.length - 1) === '"'
        ) {
            textoTratado = textoTratado.slice(1, -1);
            textoTratado = textoTratado.trim();
        }

        return textoTratado;
    }

    adicionarMensagem(msg, texto) {
        if (!texto || texto.length === 0) {
            return msg;
        }

        const SEPARADOR_MSG = '\n';
        msg.texto += msg.texto.length > 0 ? SEPARADOR_MSG : '';
        msg.texto += texto;

        return msg;
    }

    montarMsgFiltrados(totalNumerosValidos, totalProcessosFiltrados, nomeArquivo) {
        let texto;

        if (totalNumerosValidos === 0) {
            texto = `Nenhum processo filtrado. Não foram identificados números de processos válidos no arquivo indicado (${nomeArquivo}).`;
        } else {
            if (totalProcessosFiltrados === 0) {
                texto = 'Nenhum processo filtrado.';
            } else if (totalProcessosFiltrados === 1) {
                texto = '1 processo filtrado.';
            } else {
                texto = `${totalProcessosFiltrados} processos filtrados.`;
            }
        }

        return texto;
    }

    montarMsgDuplicados(totalNumerosDuplicados) {
        let texto;

        if (totalNumerosDuplicados > 0) {
            if (totalNumerosDuplicados === 1) {
                texto = 'Foi detectado e descartado 1 número de processo duplicado.';
            } else {
                texto = `Foram detectadas e descartadas ${totalNumerosDuplicados} duplicações de um ou mais números de processos.`;
            }
        }

        return texto;
    }

    montarMsgInvalidos(numerosInvalidos) {
        let texto;

        if (numerosInvalidos.length > 0) {
            if (numerosInvalidos.length === 1) {
                texto = `Foi detectado 1 número de processo inválido: ${numerosInvalidos[0]}.`;
            } else {
                texto = `Foram detectados ${numerosInvalidos.length} números de processos inválidos: ${numerosInvalidos.join(', ')}.`;
            }
        }

        return texto;
    }

    montarMsgNaoEncontrados(totalNumerosValidos, processosNaoEncontrados) {
        let texto;

        if (processosNaoEncontrados.length > 0) {
            if (totalNumerosValidos === 1) {
                texto = `O processo (${processosNaoEncontrados[0]}) não foi encontrado.`;
            } else {
                texto = `${processosNaoEncontrados.length} dos ${totalNumerosValidos} processos válidos informados não ${processosNaoEncontrados.length > 1 ? 'foram encontrados' : 'foi encontrado'}: ${processosNaoEncontrados.join(', ')}.`;
            }
        }

        return texto;
    }

    montarMsgTextosDescartados(totalParticulasTextuaisDescartadas) {
        let texto;

        if (totalParticulasTextuaisDescartadas > 0) {
            if (totalParticulasTextuaisDescartadas === 1) {
                texto = 'Foi descartada uma partícula textual por não conter dígitos.';
            } else {
                texto = `Foram descartadas ${totalParticulasTextuaisDescartadas} partículas textuais por não conterem dígitos.`;
            }
        }

        return texto;
    }

    render() {
        return (
            <div>
                <Tooltip
                    onClick={this.handleClick}
                    title={
                        <Typography>
                            Filtrar lista de números de processos a partir de um arquivo texto ou CSV
                        </Typography>
                    }
                >
                    <div>
                        <input
                            hidden
                            id="upload_lista_numeros_processos"
                            accept=".csv, .txt"
                            type="file"
                            ref={this.inputRef}
                            onChange={this.handleChange}
                        />
                        <FilterAltIcon sx={{ cursor: 'pointer' }} />
                    </div>
                </Tooltip>
            </div>
        );
    }
}

export default inject('stores')(observer(FiltrarPorNumerosProcessos));
